/*
Record-level refusals of a completed step (XOL-596, Carlos 2026-10-14; API: XOL-595
plan 4.10).

A step whose party-1 row is completed is read-only, and the API refuses a write that
belongs to it with 403 `step_completed`. A FIELD or DECISION refusal carries the rows
and is handled by the field-save flow (lib/requestRowSync, mixins/RequestMixins). A
RECORD-level refusal (a file action, equipment, a backhaul line, Create Sharer, a
stale screen's Lock) carries only `{code, message, error, stepNo}` and the rows of
no editor: the editor stays as the user left it, and the request drawer underneath
re-reads the request, which freezes the step on screen, and then says why in ONE
section of the C-12 card.

This module is the small bus between the call sites (PostMixins, FileUpload and the
editors' failure callbacks) and the one request drawer that is open:

    isStepCompleted(err)        is this axios error a 403 step_completed?
    onStepCompleted(fn, accepts)
                                a request drawer registers; returns the off function.
                                `accepts(scope)` says whether the refusal came from ITS
                                request (several drawers can be mounted: a request drawer
                                over a site drawer, RequestTable detail rows)
    hasListeners(scope)         does a drawer take this refusal? (PostMixins raises its own
                                notice when none does, so a refusal is never silent)
    reportStepCompleted(err, what, scope)
                                tell the matching drawers: { stepNo, what, scope }, `what`
                                a key of requestSync.completed.what.*; true when one took it
    scopeOf(err, body)          where a refusal came from: { actionStepId, actionId } read
                                from the request body that was refused and from the
                                refusal's own `action` rows when it carries them
    fallbackNotice(err, canReopen)
                                the { title, message } for a refusal nobody took

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { copyText } from '@/lib/requestSyncCopy'

var listeners = []

export function isStepCompleted (err) {
  var response = err && err.response
  var data = response && response.data

  return !!response && response.status === 403 && !!data && typeof data === 'object' && data.code === 'step_completed'
}

/**
 * Where a refusal came from: the step the write was made from (`actionStepId`, in the
 * body for equipment / backhaul / sharer / General Remarks) and/or the request (`actionId`:
 * a file row's `process_id`, or the refusal's own `action` rows). Either may be missing.
 */
export function scopeOf (err, body) {
  var data = err && err.response && err.response.data
  var scope = {}

  if (body && body.actionStepId !== undefined && body.actionStepId !== null) {
    scope.actionStepId = Number(body.actionStepId)
  }

  if (body && body.process_id !== undefined && body.process_id !== null) {
    scope.actionId = Number(body.process_id)
  } else if (data && data.action && data.action.id !== undefined) {
    scope.actionId = Number(data.action.id)
  }

  return scope
}

export function onStepCompleted (fn, accepts) {
  var entry = { fn: fn, accepts: accepts || null }

  listeners.push(entry)

  return function () {
    listeners = listeners.filter(function (l) { return l !== entry })
  }
}

function takers (scope) {
  return listeners.filter(function (l) { return !l.accepts || l.accepts(scope || {}) })
}

export function hasListeners (scope) {
  return takers(scope).length > 0
}

export function reportStepCompleted (err, what, scope) {
  if (!isStepCompleted(err)) {
    return false
  }

  var merged = scopeOf(err)

  Object.keys(scope || {}).forEach(function (k) {
    if (scope[k] !== undefined && scope[k] !== null) {
      merged[k] = scope[k]
    }
  })

  var list = takers(merged)

  if (list.length === 0) {
    return false
  }

  var info = { stepNo: Number(err.response.data.stepNo), what: what, scope: merged }

  list.forEach(function (l) {
    try {
      l.fn(info)
    } catch (e) {
      console.error('stepCompleted: a listener threw', e)
    }
  })

  return true
}

/**
 * The notice for a refusal no request drawer took (a site page's file removal, say): the
 * user has permission, the STEP is complete, so it must not read "You do not have
 * permission". No name (there is no request to read who completed it from).
 */
export function fallbackNotice (err, canReopen) {
  var n = Number(err && err.response && err.response.data && err.response.data.stepNo)
  var what = copyText('requestSync.completed.what.field')

  return {
    title: n ? copyText('requestSync.completed.title', { n: n }) : 'This step is complete',
    message: (n
      ? copyText('requestSync.completed.lead', { n: n, by: null, what: what }) + ' ' +
        copyText('requestSync.completed.footer', { n: n, canReopen: !!canReopen, kind: 'change' })
      : 'This step has been completed, so your change wasn\'t saved. A manager can re-open it if this still needs to change.')
  }
}

export function _resetForTests () {
  listeners = []
}
